import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../src/config";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import Sidebar from "./Sidebar";

//. Small reusable BookingCard component
const BookingCard = ({ booking }) => {
  const {
    stay,
    room,
    summary: {
      check_in,
      check_out,
      guest_count,
      nights,
      room_count,
      total_price,
      booking_code,
    } = {},
  } = booking;

  return (
    <div className="trip-card" key={booking.id}>
      <div className="trip-card-body">
        {/* Room Image */}
        {room?.images?.[0]?.url && (
          <img
            src={room.images[0].url}
            alt={room.title}
            style={{
              width: "100%",
              maxHeight: "200px",
              objectFit: "cover",
              borderRadius: "8px",
              marginBottom: "10px",
            }}
          />
        )}

        {/* Stay Info */}
        <h5>{stay?.location?.location_name}</h5>
        <div className="date">
          <span>
            {check_in ? new Date(check_in).toLocaleDateString("en-GB", { timeZone: "UTC" }) : "--"} -{" "}
            {check_out ? new Date(check_out).toLocaleDateString("en-GB", { timeZone: "UTC" }) : "--"}
          </span>
          <span>{guest_count || 0} Guests</span>
        </div>

        <p>
          <strong>Stay:</strong> {stay?.title || "--"}
        </p>
        <p>
          <strong>Address:</strong> {stay?.address || "--"}
        </p>

        {/* Room Info */}
        <p>
          <strong>Room:</strong> {room?.title || "--"}
        </p>
        <p>
          <strong>Price/Night:</strong> ₹
          {room?.price_per_night
            ? parseFloat(room.price_per_night).toLocaleString()
            : 0}
          <br />
          <strong>Tax/Night:</strong> ₹
          {room?.tax_per_night
            ? parseFloat(room.tax_per_night).toLocaleString()
            : 0}
        </p>

        {/* Summary */}
        <p>
          <strong>Nights:</strong> {nights || 0}
        </p>
        <p>
          <strong>Room Count:</strong> {room_count || 0}
        </p>
        <p>
          <strong>Total Price:</strong> ₹
          {total_price ? parseFloat(total_price).toLocaleString() : 0}
        </p>
      </div>

      {/* Footer with Details Button */}
      <div className="trip-card-footer">
        {booking_code && (
          <Link to={`/booking-details/${booking_code}`} className="theme-btn">
            View Details
          </Link>
        )}
      </div>
    </div>
  );
};

//. Main Component
const MyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedBookingCode, setSelectedBookingCode] = useState(null);

  // 🔑 Token Handling
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const token = storedUser.accessToken || localStorage.getItem("accessToken");

  //. Logout Handler
  const handleLogout = () => {
    localStorage.clear();
    navigate("/"); // Redirect to home or login page
  };


  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await axios.post(
          `${API_BASE_URL}bookings/list`,
          {},
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.success) {
          if (response.data.status === "fetched") {
            const bookingList = response.data.data?.bookings || [];
            setBookings(bookingList);
          } else if (response.data.status === "not_found") {
            setBookings([]); //. ensure no crash, keep sidebar visible
          } else {
            setError(response.data.message || "Something went wrong");
          }
        } else {
          setError(response.data.message || "Failed to fetch bookings");
        }
      } catch (err) {
        console.error("Error fetching bookings:", err);
        if (err.response?.status === 404) {
          setBookings([]); //. still show sidebar
        } else if (err.response?.status === 401) {
          setError("Access token missing or invalid. Please login again.");
        } else {
          setError("Something went wrong while fetching bookings");
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchBookings();
    } else {
      setError("User not logged in");
      setLoading(false);
    }
  }, [token]);

  const handleCancelBooking = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}bookings/cancel`,
        { booking_id: selectedBookingCode },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setBookings((prev) =>
          prev.map((b) =>
            b.summary.booking_code === selectedBookingCode
              ? {
                ...b,
                summary: {
                  ...b.summary,
                  status: "cancelled",
                },
              }
              : b
          )
        );

        toast.success("Booking cancelled successfully");
        setShowCancelModal(false);
      } else {
        setShowCancelModal(false);
        toast.error(response?.data?.message || "Failed to cancel booking");
      }
    } catch (error) {
      setShowCancelModal(false);
      console.error("Cancel booking error:", error);
      toast.error(
        error?.response?.data?.message ||
        "Something went wrong while cancelling booking"
      );
    }
  };

  const openCancelModal = (bookingId) => {
    setSelectedBookingCode(bookingId);
    setShowCancelModal(true);
  };


  const statusConfig = {
    pending: {
      text: "Pending Payment",
      bg: "#fff3cd",
      color: "#856404",
    },
    confirmed: {
      text: "Confirmed",
      bg: "#e7f1ff",
      color: "#0d6efd",
    },
    checked_in: {
      text: "Checked In",
      bg: "#e9f7ef",
      color: "#1e7e34",
    },
    checked_out: {
      text: "Checked Out",
      bg: "#f2f2f2",
      color: "#495057",
    },
    cancelled: {
      text: "Cancelled",
      bg: "#fdecea",
      color: "#842029",
    },
    no_show: {
      text: "No-Show",
      bg: "#fdecea",
      color: "#842029",
    },
    expired: {
      text: "Expired",
      bg: "#eee",
      color: "#6c757d",
    },
  };


  // 🔄 Loading & Error States
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <ClipLoader size={45} />
      </div>
    );
  }
  if (error) return <p className="error-message">{error}</p>;

  return (
    <section className="profilecontainer">
      <div className="container">
        <div className="row">
          {/* Sidebar */}
          <Sidebar />

          {/* Main Content */}
          <div className="col-xxl-9 col-xl-9 col-lg-8 col-md-9 col-sm-12 col-12">
            <div className="profilerightouter">
              <div className="dltabsview">
                {/* Tabs */}
                <ul className="nav nav-tabs" id="myTab" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link active"
                      id="Upcoming-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#Upcoming-tab-pane"
                      type="button"
                      role="tab"
                      aria-controls="Upcoming-tab-pane"
                      aria-selected="true"
                    >
                      My Bookings
                    </button>
                  </li>
                </ul>

                {/* Tab Content */}
                <div className="tab-content" id="myTabContent" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                  {/* Upcoming Bookings */}
                  <div
                    className="tab-pane fade show active"
                    id="Upcoming-tab-pane"
                    role="tabpanel"
                    aria-labelledby="Upcoming-tab"
                    tabIndex="0"
                  >
                    <div className="tabinner">
                      {bookings.length === 0 ? (
                        <div className="text-center py-5">
                          <h4 className="fw-bold">No Bookings Yet!</h4>
                          <p className="text-muted mb-3">
                            You don’t have any bookings at the moment. Start exploring properties and make your first booking today.
                          </p>
                          <Link to="/" className="theme-btn">
                            Browse Properties
                          </Link>
                        </div>
                      ) : (
                        bookings.map((booking) => (
                          <div className="triplist d-flex justify-content-between align-items-start mb-3 p-3 bg-white shadow-sm rounded" key={booking.id}>
                            {/* Left Side */}
                            <div className="triplistlt">
                              <h5>{booking.stay.location?.location_name || "--"}</h5>
                              <div className="date mb-1">
                                <span>
                                  {booking.summary.check_in
                                    ? new Date(booking.summary.check_in).toLocaleDateString("en-GB", { timeZone: "UTC" })
                                    : "--"}{" "}
                                  -{" "}
                                  {booking.summary.check_out
                                    ? new Date(booking.summary.check_out).toLocaleDateString("en-GB", { timeZone: "UTC" })
                                    : "--"}
                                </span>
                                <span>{booking.summary.guest_count || 0} People</span>
                              </div>
                              <p className="mb-1">{booking.stay.title || "--"}</p>
                              <p className="mb-0">{booking.stay.address || "--"}</p>
                            </div>

                            {/* Right Side */}
                            <div className="triplistrt d-flex flex-column gap-2">
                              {booking.summary.booking_code && (
                                <Link
                                  to={`/booking-details/${booking.summary.booking_code}`}
                                  className="theme-btn"
                                >
                                  View Details
                                </Link>
                              )}

                              {/* {booking.summary.status === "confirmed" ? (
                                <button
                                  className="theme-btn btn-danger"
                                  onClick={() => openCancelModal(booking.summary.booking_code)}
                                >
                                  Cancel Booking
                                </button>
                              ) : (
                                <span
                                  style={{
                                    display: "inline-block",
                                    padding: "4px 10px",
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    backgroundColor:
                                      statusConfig[booking.summary.status]?.bg || "#eee",
                                    color:
                                      statusConfig[booking.summary.status]?.color || "#333",
                                  }}
                                >
                                  Status :{" "}
                                  {statusConfig[booking.summary.status]?.text || "Unknown"}
                                </span>
                              )} */}
                              <div className="triplistrt d-flex flex-column gap-2">

                                {/*. CONFIRMED → Cancel button */}
                                {booking.summary.status === "confirmed" && (
                                  <button
                                    className="theme-btn btn-danger"
                                    onClick={() => openCancelModal(booking.id)}
                                  >
                                    Cancel Booking
                                  </button>
                                )}

                                {/*. PENDING → Payment button */}
                                {booking.summary.status === "pending" &&
                                  booking.summary.payment_url && (
                                    <a
                                      href={booking.summary.payment_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="theme-btn btn-warning"
                                    >
                                      Complete Payment
                                    </a>
                                  )}

                                {/*. STATUS (ALWAYS BELOW BUTTON) */}
                                <span
                                  style={{
                                    display: "inline-block",
                                    padding: "4px 10px",
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    backgroundColor:
                                      statusConfig[booking.summary.status]?.bg || "#eee",
                                    color:
                                      statusConfig[booking.summary.status]?.color || "#333",
                                  }}
                                >
                                  Status :{" "}
                                  {statusConfig[booking.summary.status]?.text || booking.summary.status}
                                </span>

                              </div>
                            </div>




                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Cancelled Bookings */}
                  <div
                    className="tab-pane fade"
                    id="Cancelled-tab-pane"
                    role="tabpanel"
                    aria-labelledby="Cancelled-tab"
                    tabIndex="0"
                  >
                    {/* Filter bookings with status 'cancelled' */}
                    <div className="tabinner">
                      {bookings.filter(b => b.summary.status === "cancelled").length === 0 ? (
                        <p className="text-center py-3">No cancelled bookings.</p>
                      ) : (
                        bookings
                          .filter(b => b.summary.status === "cancelled")
                          .map((booking) => (
                            <div className="triplist d-flex justify-content-between align-items-start mb-3 p-3 bg-white shadow-sm rounded" key={booking.id}>
                              <div className="triplistlt">
                                <h5>{booking.stay.location?.location_name || "--"}</h5>
                                <div className="date mb-1">
                                  <span>
                                    {booking.summary.check_in
                                      ? new Date(booking.summary.check_in).toLocaleDateString("en-GB", { timeZone: "UTC" })
                                      : "--"}{" "}
                                    -{" "}
                                    {booking.summary.check_out
                                      ? new Date(booking.summary.check_out).toLocaleDateString("en-GB", { timeZone: "UTC" })
                                      : "--"}
                                  </span>
                                  <span>{booking.summary.guest_count || 0} People</span>
                                </div>
                                <p className="mb-1">{booking.stay.title || "--"}</p>
                                <p className="mb-0">{booking.stay.address || "--"}</p>
                              </div>
                              <div className="triplistrt">
                                {booking.summary.booking_code && (
                                  <Link
                                    to={`/booking-details/${booking.summary.booking_code}`}
                                    className="theme-btn"
                                  >
                                    View Details
                                  </Link>
                                )}
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                  </div>

                  {/* Completed Bookings */}
                  <div
                    className="tab-pane fade"
                    id="Completed-tab-pane"
                    role="tabpanel"
                    aria-labelledby="Completed-tab"
                    tabIndex="0"
                  >
                    <div className="tabinner">
                      {bookings.filter(b => b.summary.status === "checked_out").length === 0 ? (
                        <p className="text-center py-3">No completed bookings.</p>
                      ) : (
                        bookings
                          .filter(b => b.summary.status === "checked_out")
                          .map((booking) => (
                            <div className="triplist d-flex justify-content-between align-items-start mb-3 p-3 bg-white shadow-sm rounded" key={booking.id}>
                              <div className="triplistlt">
                                <h5>{booking.stay.location?.location_name || "--"}</h5>
                                <div className="date mb-1">
                                  <span>
                                    {booking.summary.check_in
                                      ? new Date(booking.summary.check_in).toLocaleDateString("en-GB", { timeZone: "UTC" })
                                      : "--"}{" "}
                                    -{" "}
                                    {booking.summary.check_out
                                      ? new Date(booking.summary.check_out).toLocaleDateString("en-GB", { timeZone: "UTC" })
                                      : "--"}
                                  </span>
                                  <span>{booking.summary.guest_count || 0} People</span>
                                </div>
                                <p className="mb-1">{booking.stay.title || "--"}</p>
                                <p className="mb-0">{booking.stay.address || "--"}</p>
                              </div>
                              <div className="triplistrt">
                                {booking.summary.booking_code && (
                                  <Link
                                    to={`/booking-details/${booking.summary.booking_code}`}
                                    className="theme-btn"
                                  >
                                    View Details
                                  </Link>
                                )}
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>



        </div>
      </div>
      {showCancelModal && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ zIndex: 1055, pointerEvents: "auto" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Cancel Booking</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCancelModal(false)}
                  ></button>
                </div>

                <div className="modal-body">
                  <p>
                    Are you sure you want to cancel this booking?
                    <br />
                    <strong>This action cannot be undone.</strong>
                  </p>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowCancelModal(false)}
                  >
                    No
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={handleCancelBooking}
                  >
                    Yes, Cancel Booking
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Backdrop */}
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1050 }}
          ></div>
        </>
      )}
    </section>

  );
};

export default MyBookings;
